import { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState, type ReactNode, type Ref } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowDown, ArrowUp, Bell, Compass, FileText, SendHorizontal, Sparkles, Wrench } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { generateMdx } from '../lib/markdown';
import { AGENT_TURN_DONE_EVENT, parseSlash, whatItDoes } from '../lib/agents';
import { capturePage } from '../lib/pageContext';
import type { AgentMessage, Agent } from '../lib/types';
import ChatQuestion from './ChatQuestion';
import FieldProposal from './FieldProposal';

function navigationPath(m: AgentMessage): string | null {
    try { return JSON.parse(m.content).path ?? null; } catch { return null; }
}

function TypingDots() {
    return (
        <span className="inline-flex items-center gap-1 py-1">
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-coolslate" />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-coolslate" style={{ animationDelay: '0.15s' }} />
            <span className="typing-dot w-1.5 h-1.5 rounded-full bg-coolslate" style={{ animationDelay: '0.3s' }} />
        </span>
    );
}

export interface AgentChatHandle {
    /** `chatId` names a conversation the caller has just created for it; otherwise the current one. */
    send: (text: string, chatId?: string) => Promise<void>;
    reset: () => void;
    /** Reloads the conversation, for a change made outside a turn. */
    reload: () => void;
}

interface AgentChatProps {
    ref?: Ref<AgentChatHandle>;
    /** The conversation, or null until the first message creates one. */
    chatId: string | null;
    ensureChat: () => Promise<string | null>;
    agents: Agent[];
    /** A bare `/<key>` switches skill without sending anything. */
    onSwitch: (key: string) => void;
    onTurnDone?: (skill: string | null) => void;
    /** The agent moved the person to another page during the turn. */
    onNavigate?: () => void;
    emptyState?: ReactNode;
    /** Beside the buttons that step through the person's questions, such as the skill picker. */
    composerHeader?: ReactNode;
    /** Under that row, such as the attached documents. */
    composerFooter?: ReactNode;
    inputAdornment?: ReactNode;
    placeholder?: string;
    onOpenDocument?: (doc: AgentMessage) => void;
    openDocumentId?: string | null;
}

function slashMatches(input: string, agents: Agent[]): Agent[] | null {
    const m = /^\/([a-z0-9-]*)$/.exec(input);
    if (!m) return null;
    const typed = m[1];
    return agents.filter(s => s.key.startsWith(typed) || s.name.toLowerCase().includes(typed));
}

// A turn streams one JSON event per line: `{text}` for each piece of the reply being
// written, shown live as markdown, and `{message}` for each message it saves, shown as
// it will stay. The persisted messages are reloaded once the turn completes.
type TurnEvent = { text: string } | { message: AgentMessage };

// The roles a turn writes before its closing assistant message.
const TURN_ROLES = new Set(['user', 'trigger', 'activity', 'document', 'navigation', 'proposal', 'question']);
const TURN_TIMEOUT_MS = 10 * 60 * 1000;

export default function AgentChat({
    ref, chatId, ensureChat, agents, onSwitch, onTurnDone, onNavigate,
    emptyState, composerHeader, composerFooter, inputAdornment, onOpenDocument, openDocumentId, placeholder = 'Type a message, or / for a skill…',
}: AgentChatProps) {
    const { authFetch } = useAuth();
    const [messages, setMessages] = useState<AgentMessage[]>([]);
    const [input, setInput] = useState('');
    const [streaming, setStreaming] = useState<string>('');
    const [busy, setBusy] = useState(false);
    const navigate = useNavigate();
    // The agent moved the person during the turn just finished: go to the last page it named.
    const followNavigation = (history: AgentMessage[]) => {
        const lastAsk = history.map(m => m.role).lastIndexOf('user');
        const moves = history.slice(lastAsk + 1).filter(m => m.role === 'navigation');
        const path = moves.length ? navigationPath(moves[moves.length - 1]) : null;
        if (!path) return;
        navigate(path);
        onNavigate?.();
    };
    const [error, setError] = useState<string | null>(null);
    const [highlight, setHighlight] = useState(0);
    const endRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    // A conversation this component's own send created already shows the optimistic message.
    const createdHere = useRef<string | null>(null);

    const nameOf = (key: string | null | undefined) =>
        agents.find(s => (key ? s.key === key : s.default))?.name ?? (key ? `/${key}` : 'Navaris');

    useEffect(() => {
        if (!chatId) { setMessages([]); return; }
        if (createdHere.current === chatId) return;
        let stale = false;
        authFetch(`/agents/chats/${chatId}`)
            .then(r => r.ok ? r.json() : null)
            .then(data => { if (!stale && data) setMessages(data.messages ?? []); })
            .catch(() => {});
        return () => { stale = true; };
    }, [chatId, authFetch]);

    useEffect(() => {
        endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [messages, streaming]);

    // Refocus the message box when a turn finishes (busy true -> false) so the person can reply straight away.
    const wasBusy = useRef(false);
    useEffect(() => {
        if (wasBusy.current && !busy) inputRef.current?.focus();
        wasBusy.current = busy;
    }, [busy]);

    // Stepping through the person's questions: each is marked `data-question`, and
    // the list scrolls to put the one before or after the top of the view at the top.
    const listRef = useRef<HTMLDivElement>(null);
    const [around, setAround] = useState({ before: false, after: false });
    const questionTops = () => {
        const list = listRef.current;
        if (!list) return [];
        const top = list.getBoundingClientRect().top - list.scrollTop;
        return [...list.querySelectorAll<HTMLElement>('[data-question]')]
            .map(el => el.getBoundingClientRect().top - top);
    };
    // A question scrolled to sits below the list's top padding; within a few pixels of
    // there counts as being at it, so each click moves on to the next one.
    const GAP = 16;
    const NEAR = 8;
    const measureQuestions = () => {
        const list = listRef.current;
        if (!list) return;
        const view = list.scrollTop + GAP;
        const atEnd = list.scrollTop >= list.scrollHeight - list.clientHeight - 1;
        const tops = questionTops();
        setAround({
            before: tops.some(t => t < view - NEAR),
            after: !atEnd && tops.some(t => t > view + NEAR),
        });
    };
    const toQuestion = (direction: -1 | 1) => {
        const list = listRef.current;
        if (!list) return;
        const view = list.scrollTop + GAP;
        const tops = questionTops();
        const target = direction < 0
            ? [...tops].reverse().find(t => t < view - NEAR)
            : tops.find(t => t > view + NEAR);
        if (target !== undefined) list.scrollTo({ top: Math.max(0, target - GAP), behavior: 'smooth' });
    };
    useEffect(measureQuestions, [messages, streaming]);

    const matches = slashMatches(input, agents);
    useEffect(() => { setHighlight(0); }, [input]);

    // Set synchronously, so two sends in one tick can't both get through.
    const sendingRef = useRef(false);
    // Bumped by reset(); a reply still streaming from the previous conversation checks it before writing.
    const shownRef = useRef(0);

    // A turn runs to the end on the server whether or not its page is open, so a
    // conversation reopened partway through one shows it working until its reply lands.
    const last = messages[messages.length - 1];
    const working = !busy && !!chatId && !!last && TURN_ROLES.has(last.role)
        && Date.now() - Date.parse(last.created_at) < TURN_TIMEOUT_MS;
    useEffect(() => {
        if (!working || !chatId) return;
        const conversation = shownRef.current;
        const timer = setTimeout(() => {
            authFetch(`/agents/chats/${chatId}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (!data || conversation !== shownRef.current) return;
                    setMessages(data.messages ?? []);
                    if (!TURN_ROLES.has(data.messages?.[data.messages.length - 1]?.role)) {
                        window.dispatchEvent(new CustomEvent(AGENT_TURN_DONE_EVENT));
                    }
                })
                .catch(() => {});
        }, 3000);
        return () => clearTimeout(timer);
    }, [working, messages, chatId, authFetch]);

    const send = async (text?: string, intoChat?: string) => {
        const raw = (text ?? input).trim();
        if (!raw || busy) return;
        setError(null);

        const [key, rest] = parseSlash(raw);
        if (raw.startsWith('/') && (!key || !agents.some(s => s.key === key))) {
            const word = raw.split(/\s/)[0];
            setError(`There's no skill called ${word}. Type / to see them.`);
            return;
        }
        if (key && !rest.trim()) {
            onSwitch(key);
            if (text === undefined) setInput('');
            return;
        }
        if (sendingRef.current) return;
        sendingRef.current = true;
        const conversation = shownRef.current;
        const current = () => conversation === shownRef.current;
        if (text === undefined) setInput('');
        setBusy(true);
        setMessages(prev => [...prev, {
            id: `local-${Date.now()}`, chat_id: chatId ?? '', role: 'user',
            skill: key, content: key ? rest.trim() : raw,
            position: prev.length, created_at: new Date().toISOString(),
        }]);
        if (intoChat) createdHere.current = intoChat;
        let cid = intoChat ?? chatId;
        let streamError = false;
        try {
            if (!cid) {
                cid = await ensureChat();
                if (!cid) throw new Error('Could not start a conversation');
                createdHere.current = cid;
            }
            const res = await authFetch(`/agents/chats/${cid}/messages`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ content: raw, page: capturePage() }),
            });
            if (!res.ok || !res.body) throw new Error('The agent could not respond');
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let pending = '';
            let live = '';
            const apply = (event: TurnEvent) => {
                if (!current()) return;
                if ('text' in event) {
                    live += event.text;
                    setStreaming(live);
                    return;
                }
                const row = event.message;
                // A saved reply is the text streamed so far, now kept.
                if (row.role === 'assistant') { live = ''; setStreaming(''); }
                // A row seen again (an activity note with what its tool found) keeps its place.
                setMessages(prev => prev.some(m => m.id === row.id)
                    ? prev.map(m => (m.id === row.id ? row : m))
                    : [...prev.filter(m => !(row.role === 'user' && m.id.startsWith('local-'))), row]);
            };
            for (;;) {
                const { done, value } = await reader.read();
                if (value) pending += decoder.decode(value, { stream: true });
                const lines = pending.split('\n');
                pending = lines.pop() ?? '';
                for (const line of lines) if (line.trim()) apply(JSON.parse(line) as TurnEvent);
                if (done) break;
            }
        } catch {
            // The stream may break after the agent persisted its reply; the reconcile below loads the real state.
            streamError = true;
        } finally {
            // Reconcile to the persisted history, the canonical state, however the stream ended.
            let reconciled = false;
            if (cid && current()) {
                try {
                    const chatRes = await authFetch(`/agents/chats/${cid}`);
                    if (chatRes.ok && current()) {
                        const data = await chatRes.json();
                        setMessages(data.messages ?? []);
                        followNavigation(data.messages ?? []);
                        onTurnDone?.(data.skill ?? null);
                        reconciled = true;
                    }
                } catch {}
            }
            if (streamError && !reconciled && current()) {
                setMessages(prev => [...prev, {
                    id: `err-${Date.now()}`, chat_id: cid ?? chatId ?? '', role: 'assistant',
                    content: '_Sorry - something went wrong generating a response._',
                    position: prev.length, created_at: new Date().toISOString(),
                }]);
            }
            window.dispatchEvent(new CustomEvent(AGENT_TURN_DONE_EVENT));
            // Always cleared, so a turn can't leave the window busy or showing half-streamed text.
            sendingRef.current = false;
            setStreaming('');
            setBusy(false);
        }
    };

    useImperativeHandle(ref, () => ({
        send: (text, chatId) => send(text, chatId),
        reset: () => {
            shownRef.current += 1;
            sendingRef.current = false;
            setBusy(false);
            setStreaming('');
            setMessages([]);
            setInput('');
            setError(null);
            createdHere.current = null;
        },
        reload: () => {
            if (!chatId || busy) return;
            const conversation = shownRef.current;
            authFetch(`/agents/chats/${chatId}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => { if (data && conversation === shownRef.current) setMessages(data.messages ?? []); })
                .catch(() => {});
        },
    }));

    const choose = (s: Agent) => setInput(`/${s.key} `);

    // The message box grows with what is typed, up to its max-height, then scrolls. It is
    // fitted again when its width changes, since that moves where the lines wrap.
    useLayoutEffect(() => {
        const el = inputRef.current;
        if (el) fitToText(el);
    }, [input]);
    useEffect(() => {
        const el = inputRef.current;
        if (!el) return;
        let width = el.clientWidth;
        const observer = new ResizeObserver(() => {
            if (el.clientWidth === width) return;
            width = el.clientWidth;
            fitToText(el);
        });
        observer.observe(el);
        return () => observer.disconnect();
    }, []);

    const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (matches && matches.length > 0) {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight(h => (h + 1) % matches.length); return; }
            if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => (h - 1 + matches.length) % matches.length); return; }
            if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); choose(matches[highlight] ?? matches[0]); return; }
            if (e.key === 'Escape') { e.preventDefault(); setInput(''); return; }
        }
        // Shift+Enter adds a line; Enter while an input method is composing belongs to it.
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); send(); }
    };

    const documentLink = (m: AgentMessage) => {
        const open = m.id === openDocumentId;
        return (
            <div key={m.id} className="flex justify-start">
                <button
                    type="button"
                    onClick={() => onOpenDocument?.(m)}
                    aria-pressed={open}
                    className={`max-w-[80%] flex items-center gap-2 px-3 py-2 rounded-xl border text-left text-sm transition-colors ${
                        open ? 'bg-brand/10 border-brand/40 text-brand-dark'
                             : 'bg-white border-softgrey text-graphite hover:border-brand/40'}`}
                >
                    <FileText className="w-4 h-4 shrink-0 text-brand-dark" />
                    <span className="font-medium">{m.title || 'Document'}</span>
                </button>
            </div>
        );
    };

    // A document, proposal or question is made before the reply that introduces it, so it is shown after that reply.
    const ordered: AgentMessage[] = [];
    let held: AgentMessage[] = [];
    for (const m of messages) {
        if (m.role === 'document' || m.role === 'proposal' || m.role === 'question' || m.role === 'navigation') { held.push(m); continue; }
        if (m.role === 'user' || m.role === 'trigger') { ordered.push(...held); held = []; }
        ordered.push(m);
        if (m.role === 'assistant') { ordered.push(...held); held = []; }
    }
    ordered.push(...held);

    let lastSpeaker: string | null | undefined;
    const rendered = ordered.map(m => {
        if (m.role === 'document') return documentLink(m);
        if (m.role === 'proposal') return <FieldProposal key={m.id} message={m} />;
        if (m.role === 'question') {
            const answered = busy || messages.some(r => r.role === 'user' && r.position > m.position);
            return <ChatQuestion key={m.id} message={m} answered={answered} onAnswer={answer => send(answer)} />;
        }
        if (m.role === 'navigation') {
            return (
                <div key={m.id} className="flex justify-start">
                    <Link to={navigationPath(m) ?? '/'} className="flex items-center gap-1.5 text-[12px] italic text-coolslate hover:text-brand-dark px-3 py-1">
                        <Compass className="w-3.5 h-3.5 shrink-0" aria-hidden />
                        Moving to {m.title}
                    </Link>
                </div>
            );
        }
        if (m.role === 'trigger') {
            return (
                <div key={m.id} data-question="" className="flex justify-start">
                    <div className="flex items-start gap-1.5 text-[12px] text-coolslate px-3 py-1">
                        <Bell className="w-3.5 h-3.5 shrink-0 mt-0.5 text-brand-dark" aria-hidden />
                        <span><span className="font-medium text-graphite">Watch:</span> {m.content}</span>
                    </div>
                </div>
            );
        }
        // The backend writes these ready to show: a tool used, or a switch of skill.
        if (m.role === 'activity' || m.role === 'skill') {
            const Icon = m.role === 'skill' ? Sparkles : Wrench;
            return (
                <div key={m.id} className="flex justify-start">
                    <div className="flex items-center gap-1.5 text-[12px] italic text-coolslate px-3 py-1">
                        <Icon className="w-3.5 h-3.5 shrink-0" aria-hidden />
                        {m.content}
                    </div>
                </div>
            );
        }
        const mine = m.role === 'user';
        let label: string | null = null;
        if (!mine) {
            const speaker = m.skill ?? null;
            if (speaker !== lastSpeaker) label = nameOf(speaker);
            lastSpeaker = speaker;
        }
        return (
            <div key={m.id} data-question={mine ? '' : undefined} className={`flex flex-col ${mine ? 'items-end' : 'items-stretch'}`}>
                {label && <span className="text-[11px] font-medium text-coolslate mb-1 px-1">{label}</span>}
                {/* The person's question is a bubble on the right; the reply takes the full width. */}
                <div
                    className={`px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        mine ? 'max-w-[80%] bg-brand text-white rounded-br-none'
                             : 'bg-white text-graphite border border-softgrey rounded-bl-none'}`}
                    dangerouslySetInnerHTML={{ __html: mine ? escapeHtml(m.content) : generateMdx(m.content) }}
                />
            </div>
        );
    });

    // The reply being written is in the skill of the latest switch, else of the latest reply.
    const liveSpeaker = [...messages].reverse().find(m => m.role === 'skill' || m.role === 'assistant')?.skill ?? null;

    return (
        <div className="flex flex-col bg-offwhite h-full min-h-0">
            <div ref={listRef} onScroll={measureQuestions} className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.length === 0 && !streaming && (
                    emptyState ?? (
                        <p className="navaris-empty">
                            Ask a question to get started.
                        </p>
                    )
                )}
                {rendered}
                {streaming && (
                    <div className="flex flex-col items-stretch">
                        {liveSpeaker !== lastSpeaker && (
                            <span className="text-[11px] font-medium text-coolslate mb-1 px-1">{nameOf(liveSpeaker)}</span>
                        )}
                        <div
                            className="px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed bg-white text-graphite border border-softgrey rounded-bl-none"
                            dangerouslySetInnerHTML={{ __html: generateMdx(streaming) }}
                        />
                    </div>
                )}
                {(working || (busy && !streaming)) && (
                    <div className="flex justify-start">
                        <div className="bg-white border border-softgrey rounded-2xl rounded-bl-none px-3.5 py-2.5">
                            <TypingDots />
                        </div>
                    </div>
                )}
                <div ref={endRef} />
            </div>
            <div className="relative p-3 border-t border-softgrey bg-white">
                {matches && matches.length > 0 && (
                    <ul
                        role="listbox"
                        aria-label="Skills"
                        className="absolute bottom-full left-3 right-3 mb-1 max-h-64 overflow-y-auto navaris-dropdown z-10"
                    >
                        {matches.map((s, i) => (
                            <li key={s.key} role="option" aria-selected={i === highlight}>
                                <button
                                    type="button"
                                    onMouseDown={e => { e.preventDefault(); choose(s); }}
                                    onMouseEnter={() => setHighlight(i)}
                                    className={`w-full text-left px-3 py-2 ${i === highlight ? 'bg-offwhite' : ''}`}
                                >
                                    <span className="flex items-baseline gap-2">
                                        <code className="text-xs text-brand-dark">/{s.key}</code>
                                        <span className="text-sm text-graphite">{s.name}</span>
                                    </span>
                                    {s.description && (
                                        <span className="block mt-0.5 text-xs text-coolslate leading-snug">{whatItDoes(s.description)}</span>
                                    )}
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
                <div className="mb-2 flex items-center gap-2">
                    <div className="min-w-0 flex-1">{composerHeader}</div>
                    <div className="flex items-center gap-0.5 shrink-0">
                        <button
                            type="button"
                            onClick={() => toQuestion(-1)}
                            disabled={!around.before}
                            aria-label="Previous question"
                            title="Previous question"
                            className="w-7 h-7 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-coolslate"
                        >
                            <ArrowUp className="w-4 h-4" />
                        </button>
                        <button
                            type="button"
                            onClick={() => toQuestion(1)}
                            disabled={!around.after}
                            aria-label="Next question"
                            title="Next question"
                            className="w-7 h-7 grid place-items-center rounded-md text-coolslate hover:text-brand-dark hover:bg-offwhite disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-coolslate"
                        >
                            <ArrowDown className="w-4 h-4" />
                        </button>
                    </div>
                </div>
                {composerFooter && <div className="mb-2">{composerFooter}</div>}
                {error && <p className="mb-2 text-xs text-state-error">{error}</p>}
                <div className="flex items-end gap-2">
                    <div className="relative flex-1 min-w-0">
                        {inputAdornment}
                        <textarea
                            ref={inputRef}
                            rows={1}
                            value={input}
                            onChange={e => { setInput(e.target.value); setError(null); }}
                            onKeyDown={onKeyDown}
                            placeholder={placeholder}
                            aria-label="Message"
                            className={`block w-full resize-none max-h-40 overflow-y-auto py-2 pr-3 text-sm leading-5 border border-softgrey rounded-lg focus:outline-none focus:border-brand/40 ${inputAdornment ? 'pl-10' : 'pl-3'}`}
                        />
                    </div>
                    <button
                        onClick={() => send()}
                        disabled={!input.trim() || busy}
                        aria-label="Send"
                        className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-brand hover:bg-brand-dark text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <SendHorizontal className="w-5 h-5" />
                    </button>
                </div>
            </div>
        </div>
    );
}

function fitToText(el: HTMLTextAreaElement) {
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + el.offsetHeight - el.clientHeight}px`;
}

// User messages are plain text, so they are escaped.
function escapeHtml(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}

